import { HABIT_TYPES } from '../../constants/habitTypes';
import { LOG_TYPES, getLogTypeByEntry, getTimelineMetaForCategory, isBehaviorIncidentEntry } from '../../constants/logTypeRegistry';
import { getIncidentTypeConfig, getSeverityScale } from '../../services/incidentService';
import { getCalendarDateKey } from '../../utils/calendarDateKey';

export const getEntryUser = (entry) => ({
  loggedByUser: entry.loggedBy?.name || entry.loggedByUser || entry.authorName || entry.authorEmail || null,
  userRole: entry.loggedBy?.role || entry.userRole || entry.authorRole || entry.loggedByRole || null,
  userId: entry.loggedBy?.id || entry.authorId || entry.createdBy || null,
});

const HABIT_CATEGORY_ICON_MAP = {
  mood: '🙂',
  sleep: '😴',
  nutrition: '🍎',
  progress: '📈',
  diaper: '🚽',
  bathroom: '🚽',
  medication: '💊',
  other: '📝',
};

const preserveStructuredLogFields = (dailyLog, mappedEntry) => ({
  ...dailyLog,
  ...mappedEntry,
  activityDetails: dailyLog.activityDetails || null,
  foodDetails: dailyLog.foodDetails || null,
  sleepDetails: dailyLog.sleepDetails || null,
  bathroomDetails: dailyLog.bathroomDetails || null,
  incidentData: dailyLog.incidentData && typeof dailyLog.incidentData === 'object' && Object.keys(dailyLog.incidentData).length > 0
    ? dailyLog.incidentData
    : null,
  medicationDetails: dailyLog.medicationDetails || null,
});

const isDocumentPointerDailyLog = (dailyLog = {}) => {
  const pointerType = String(
    dailyLog.timelineType
    || dailyLog.type
    || dailyLog.category
    || dailyLog.pointerKind
    || ''
  ).trim().toLowerCase();

  return pointerType === 'document_pointer'
    || pointerType === 'document_source_pointer'
    || dailyLog.pointerKind === 'document_timeline'
    || dailyLog.pointerKind === 'document_source';
};

const getNormalizedEntryDayKey = (entry = {}) => {
  const explicitDayKey = entry.entryDayKey
    || entry.incidentDayKey
    || entry.dayKey
    || entry.localDayKey
    || entry.dateKey
    || entry.localDate
    || entry.activityDetails?.localDate
    || entry.foodDetails?.localDate
    || entry.sleepDetails?.localDate
    || entry.bathroomDetails?.localDate
    || entry.medicationDetails?.localDate
    || entry.incidentData?.localDate;

  if (explicitDayKey) {
    return getCalendarDateKey(explicitDayKey);
  }

  return getCalendarDateKey(
    entry.timestamp?.toDate?.()
    || entry.timestamp
    || entry.createdAt?.toDate?.()
    || entry.createdAt
    || entry.date?.toDate?.()
    || entry.date
  );
};

const normalizeIncidentCategory = (incidentConfig, incidentType) => {
  const normalizedType = incidentConfig?.id === 'behavioral'
    ? 'behavior'
    : (incidentConfig?.id || incidentType || 'other');

  if (incidentConfig?.id === 'behavioral') {
    return {
      categoryId: 'behavior',
      label: LOG_TYPES.behavior.displayLabel,
      color: LOG_TYPES.behavior.palette.dot,
      icon: LOG_TYPES.behavior.icon,
    };
  }

  return {
    categoryId: normalizedType,
    label: incidentConfig?.label || incidentType || 'Other',
    color: incidentConfig?.color || '#6B7280',
    icon: incidentConfig?.emoji || '📝',
  };
};

const buildIncidentSummary = (incident = {}) => {
  const severityScale = getSeverityScale(incident.type || 'other');
  const severityInfo = severityScale[incident.severity] || { label: 'Unknown' };
  const parts = [
    incident.severity ? `Severity: ${severityInfo.label} (${incident.severity}/10)` : null,
    incident.notes ? `Notes: ${incident.notes}` : null,
    incident.triggerSummary ? `Triggers: ${incident.triggerSummary}` : null,
  ].filter(Boolean);

  return parts.join(' • ');
};

export const normalizeIncidentEntry = (incident = {}, customCategories = null) => {
  const incidentConfig = getIncidentTypeConfig(incident.type, customCategories) || getIncidentTypeConfig('OTHER');
  const normalizedIncident = normalizeIncidentCategory(incidentConfig, incident.type);
  const timestamp = incident.timestamp?.toDate ? incident.timestamp.toDate() : new Date(incident.timestamp || incident.createdAt);
  const entryDayKey = getNormalizedEntryDayKey({ ...incident, timestamp });

  return {
    ...incident,
    id: incident.id,
    type: 'incident',
    timelineType: 'incident',
    collection: 'incidents',
    timestamp,
    category: normalizedIncident.categoryId,
    incidentCategoryId: normalizedIncident.categoryId,
    incidentCategoryLabel: incident.customIncidentName || normalizedIncident.label || incident.type || 'Other',
    incidentCategoryColor: normalizedIncident.color,
    incidentCategoryIcon: normalizedIncident.icon,
    content: incident.content || buildIncidentSummary(incident),
    notes: incident.notes || '',
    triggerSummary: incident.triggerSummary || incident.remedy || '',
    description: incident.description || null,
    summary: incident.summary || null,
    incidentType: incident.type,
    severity: incident.severity,
    remedy: incident.remedy || null,
    triggers: incident.triggers || [],
    duration: incident.duration || null,
    interventions: incident.interventions || [],
    mediaAttachments: incident.mediaAttachments || [],
    mediaURL: incident.mediaURL || null,
    localDate: incident.localDate || entryDayKey || null,
    entryDayKey,
    incidentDayKey: incident.incidentDayKey || incident.entryDayKey || entryDayKey || null,
    ...getEntryUser(incident),
  };
};

export const normalizeDailyLogEntry = (dailyLog = {}) => {
  if (isDocumentPointerDailyLog(dailyLog)) {
    const timestamp = dailyLog.timestamp?.toDate ? dailyLog.timestamp.toDate() : new Date(dailyLog.timestamp || dailyLog.createdAt || Date.now());
    const entryDayKey = getNormalizedEntryDayKey({ ...dailyLog, timestamp });
    return {
      ...dailyLog,
      id: dailyLog.id,
      collection: 'dailyLogs',
      timestamp,
      entryDayKey,
      localDate: dailyLog.localDate || entryDayKey || null,
      ...getEntryUser(dailyLog),
    };
  }

  const categoryType = getLogTypeByEntry(dailyLog);
  const categoryMeta = getTimelineMetaForCategory(categoryType.category, { importantMoment: !!dailyLog.importantMoment });
  const isBehaviorIncident = isBehaviorIncidentEntry(dailyLog);
  const plannerCardKey = String(
    dailyLog?.plannerCardKey
    || dailyLog?.metadata?.timelinePlanner?.plannerCardKey
    || ''
  ).trim();
  const hasPlannerMetadata = Boolean(plannerCardKey);
  const persistedTitle = String(dailyLog.title || '').trim();
  const incidentSnapshot = dailyLog.contextSnapshot || dailyLog.incidentData?.contextSnapshot || null;
  const bathroomDetails = dailyLog.bathroomDetails || null;
  const bathroomSummary = bathroomDetails?.location
    || dailyLog.location
    || bathroomDetails?.consistencyLabel
    || dailyLog.consistencyLabel
    || bathroomDetails?.type
    || dailyLog.toiletType
    || null;
  const notesText = dailyLog.notes
    || dailyLog.activityDetails?.notes
    || dailyLog.sleepDetails?.notes
    || dailyLog.bathroomDetails?.notes
    || dailyLog.incidentData?.notes
    || null;
  const severityLabel = dailyLog.severityLabel
    || dailyLog.incidentData?.severityLabel
    || (dailyLog.severity ? `Severity ${dailyLog.severity}` : null);
  const triggerSummary = dailyLog.triggerSummary
    || dailyLog.incidentData?.triggerSummary
    || null;
  const behaviorContentParts = isBehaviorIncident
    ? [
        severityLabel ? `Severity: ${severityLabel}${dailyLog.severity ? ` (${dailyLog.severity})` : ''}` : null,
        notesText ? `Notes: ${notesText}` : null,
        incidentSnapshot?.patternInsight ? incidentSnapshot.patternInsight : null,
      ].filter(Boolean)
    : [];
  const content = isBehaviorIncident
    ? behaviorContentParts.join(' • ')
    : (
      dailyLog.content
      || dailyLog.body
      || ((dailyLog.category === 'bathroom' || dailyLog.category === 'toileting')
        ? (bathroomSummary || notesText || '')
        : notesText)
    );
  const entryDayKey = getNormalizedEntryDayKey(dailyLog);
  const editedNarrativeText = (dailyLog.editedAt || dailyLog.editState === 'edited')
    ? (dailyLog.text || dailyLog.content || dailyLog.body || null)
    : null;

  return preserveStructuredLogFields(dailyLog, {
    id: dailyLog.id,
    logCategory: categoryType.category,
    type: isBehaviorIncident ? 'behavior' : categoryMeta.type,
    timelineType: isBehaviorIncident ? 'incident' : categoryMeta.timelineType,
    collection: 'dailyLogs',
    childId: dailyLog.childId,
    timestamp: dailyLog.timestamp?.toDate ? dailyLog.timestamp.toDate() : new Date(dailyLog.timestamp),
    category: categoryType.category,
    title: hasPlannerMetadata && persistedTitle
      ? persistedTitle
      : (dailyLog.importantMoment
        ? 'Important Moment'
        : (isBehaviorIncident
          ? 'Behavior'
          : (
            categoryType.category === 'medication'
              ? dailyLog.medicationName
                || dailyLog.title
                || dailyLog.titlePrefix
                || categoryMeta.titlePrefix
                || LOG_TYPES.log.displayLabel
              : (dailyLog.titlePrefix || dailyLog.title || categoryMeta.titlePrefix || LOG_TYPES.log.displayLabel)
          ))),
    titlePrefix: dailyLog.titlePrefix || categoryMeta.titlePrefix || null,
    medicationName: dailyLog.medicationName || dailyLog.medicationDetails?.medicationName || dailyLog.medicationDetails?.name || null,
    medicationScheduleDose: dailyLog.medicationScheduleDose || dailyLog.medicationDetails?.dosage || dailyLog.medicationDetails?.dose || dailyLog.dosage || dailyLog.dose || null,
    medicationScheduleUnit: dailyLog.medicationScheduleUnit || dailyLog.medicationDetails?.unit || dailyLog.unit || null,
    medicationScheduleTime: dailyLog.medicationScheduleTime || dailyLog.time || null,
    medicationCategory: dailyLog.medicationCategory || dailyLog.medicationFrequency || null,
    color: isBehaviorIncident ? LOG_TYPES.behavior.palette.dot : categoryMeta.color,
    categoryIcon: isBehaviorIncident ? LOG_TYPES.behavior.icon : categoryMeta.icon,
    text: dailyLog.text,
    body: editedNarrativeText || dailyLog.body || dailyLog.content || notesText || null,
    content,
    foodDetails: dailyLog.foodDetails || null,
    tags: dailyLog.tags,
    mediaURL: dailyLog.mediaURL,
    mediaType: dailyLog.mediaType,
    mediaUrls: dailyLog.mediaUrls,
    voiceMemoURL: dailyLog.voiceMemoURL,
    sleepDetails: dailyLog.sleepDetails || null,
    sleepType: dailyLog.sleepDetails?.type || dailyLog.sleepType || null,
    sleepStatus: dailyLog.sleepDetails?.status || dailyLog.status || null,
    sleepQuality: dailyLog.sleepDetails?.quality || null,
    sleepDuration: dailyLog.sleepDetails?.duration || dailyLog.sleepDetails?.durationMin || null,
    sleepStartAt: dailyLog.sleepDetails?.startAt || dailyLog.sleepDetails?.startTime || dailyLog.startAt || dailyLog.startTime || null,
    sleepEndAt: dailyLog.sleepDetails?.endAt || dailyLog.sleepDetails?.endTime || dailyLog.endAt || dailyLog.endTime || null,
    bedtime: dailyLog.sleepDetails?.startAt || dailyLog.sleepDetails?.startTime || dailyLog.sleepDetails?.bedtime || dailyLog.startAt || dailyLog.startTime || null,
    wakeTime: dailyLog.sleepDetails?.endAt || dailyLog.sleepDetails?.endTime || dailyLog.sleepDetails?.wakeTime || dailyLog.endAt || dailyLog.endTime || null,
    bathroomDetails,
    bathroomSummary,
    bathroomType: bathroomDetails?.type || dailyLog.toiletType || null,
    bathroomLocation: bathroomDetails?.location || dailyLog.location || null,
    bathroomConsistency: bathroomDetails?.consistency ?? dailyLog.consistency ?? null,
    bathroomConsistencyLabel: bathroomDetails?.consistencyLabel || dailyLog.consistencyLabel || null,
    displayDate: dailyLog.displayDate || dailyLog.sleepDetails?.displayDate || null,
    anchorDate: dailyLog.anchorDate || dailyLog.sleepDetails?.anchorDate || dailyLog.sleepDetails?.startAt || dailyLog.sleepDetails?.startTime || null,
    entryDate: dailyLog.entryDate || dailyLog.sleepDetails?.entryDate || null,
    entryDayKey,
    localDate: dailyLog.localDate || entryDayKey || null,
    localTime: dailyLog.localTime || dailyLog.time || null,
    time: dailyLog.time || dailyLog.localTime || null,
    timeSource: dailyLog.timeSource || dailyLog.metadata?.timeSource || null,
    timeBucket: dailyLog.timeBucket || dailyLog.metadata?.timeBucket || null,
    timeConfidence: dailyLog.timeConfidence || dailyLog.metadata?.timeConfidence || null,
    timeInferred: dailyLog.timeInferred || dailyLog.metadata?.timeInferred || false,
    timeInferenceReason: dailyLog.timeInferenceReason || dailyLog.metadata?.timeInferenceReason || null,
    notes: notesText,
    severity: dailyLog.severity,
    severityLabel,
    triggerSummary,
    remedy: dailyLog.remedy || dailyLog.incidentData?.remedy || null,
    suspectedTriggers: dailyLog.suspectedTriggers || dailyLog.incidentData?.suspectedTriggers || [],
    contextSnapshot: incidentSnapshot,
    incidentData: dailyLog.incidentData && typeof dailyLog.incidentData === 'object' && Object.keys(dailyLog.incidentData).length > 0
      ? dailyLog.incidentData
      : null,
    entryType: isBehaviorIncident ? 'incident' : dailyLog.entryType,
    incidentStyle: isBehaviorIncident,
    incidentCategoryId: isBehaviorIncident ? 'behavior' : dailyLog.incidentCategoryId,
    incidentCategoryLabel: isBehaviorIncident ? 'Behavior' : dailyLog.incidentCategoryLabel,
    incidentCategoryColor: isBehaviorIncident ? LOG_TYPES.behavior.palette.dot : dailyLog.incidentCategoryColor,
    incidentCategoryIcon: isBehaviorIncident ? undefined : dailyLog.incidentCategoryIcon,
    importantMoment: !!dailyLog.importantMoment,
    isImportantMoment: !!dailyLog.importantMoment,
    ...getEntryUser(dailyLog),
  });
};

export const normalizeDailyCareEntry = (habit = {}) => {
  const habitType = Object.values(HABIT_TYPES).find(({ id }) => id === habit.categoryId);
  const isMoodHabit = habit.actionType === 'mood' || habit.categoryId === 'mood';
  const moodValue = habit.value || habit.data?.level || habit.mood || habit.moodLevel || habit.title || 'Calm';
  const categoryLabel = isMoodHabit
    ? 'Mood'
    : (habit.categoryLabel || habitType?.label || 'Daily Habit');
  const categoryColor = isMoodHabit
    ? LOG_TYPES.mood.palette.dot
    : (habitType?.color || '#64748B');
  const categoryIcon = isMoodHabit
    ? LOG_TYPES.mood.icon
    : (HABIT_CATEGORY_ICON_MAP[habit.categoryId] || '📝');
  const entryDayKey = getNormalizedEntryDayKey(habit);

  return {
    ...habit,
    id: habit.id,
    type: isMoodHabit ? 'mood' : 'dailyHabit',
    collection: 'dailyCare',
    timestamp: habit.timestamp?.toDate ? habit.timestamp.toDate() : new Date(habit.timestamp),
    categoryId: isMoodHabit ? 'mood' : habit.categoryId,
    categoryLabel,
    categoryColor,
    categoryIcon,
    level: isMoodHabit ? moodValue : habit.level,
    notes: habit.notes,
    mediaUrls: habit.mediaUrls,
    actionType: habit.actionType || null,
    entryDayKey,
    localDate: habit.localDate || entryDayKey || null,
    moodValue: isMoodHabit ? String(moodValue) : habit.moodValue,
    moodEmoji: isMoodHabit ? LOG_TYPES.mood.icon : habit.moodEmoji,
    ...getEntryUser(habit),
  };
};
